import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { tokens } from '../theme';
import ruralCareLogo from '../assets/transparente.png';

// Login con layout split: izquierda identidad de marca, derecha formulario.
// No glassmorphism, no gradientes decorativos — claridad antes que ornamento.

const Login = () => {
  const [username,     setUsername]     = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);

  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password) {
      setError('Introduce usuario y contraseña');
      return;
    }
    setLoading(true);
    try {
      const result = await login(username.trim(), password);
      if (!result.success) setError(result.error || 'Credenciales incorrectas');
    } catch {
      setError('No se puede conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: tokens.color.ink.primary,
      }}
    >
      {/* Panel izquierdo — identidad */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          px: 8,
          backgroundColor: tokens.color.ink.primary,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Acento geométrico sutil */}
        <Box
          sx={{
            position: 'absolute',
            top: -120,
            right: -120,
            width: 400,
            height: 400,
            borderRadius: '50%',
            backgroundColor: tokens.color.brand[700],
            opacity: 0.25,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -80,
            left: -80,
            width: 280,
            height: 280,
            borderRadius: '50%',
            backgroundColor: tokens.color.brand[600],
            opacity: 0.15,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 400 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 5 }}>
            <Box
              component="img"
              src={ruralCareLogo}
              alt="RuralCare"
              sx={{
                width: 44,
                height: 44,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#fff',
                letterSpacing: '-0.01em',
              }}
            >
              RuralCare
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: '2rem',
              fontWeight: 700,
              color: '#fff',
              lineHeight: 1.2,
              mb: 1.5,
              letterSpacing: '-0.025em',
            }}
          >
            Panel de Administración
          </Typography>
          <Typography
            sx={{
              fontSize: '1rem',
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.6,
            }}
          >
            Gestión remota de tablets Android para personas mayores en zonas rurales.
          </Typography>

          <Box sx={{ mt: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[
              'Monitorea el estado de cada dispositivo en tiempo real',
              'Envía mensajes, actualiza apps y configura accesos',
              'Gestiona proyectos, usuarios y facturación',
            ].map((text) => (
              <Box key={text} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: tokens.color.brand[400],
                    flexShrink: 0,
                    mt: '6px',
                  }}
                />
                <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Panel derecho — formulario */}
      <Box
        sx={{
          width: { xs: '100%', md: '440px' },
          flexShrink: 0,
          backgroundColor: tokens.color.surface.paper,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: 3, sm: 5 },
          py: 6,
        }}
      >
        {/* Mobile header */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
          <Box
            component="img"
            src={ruralCareLogo}
            alt="RuralCare"
            sx={{
              width: 36,
              height: 36,
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: tokens.color.ink.primary }}>
            RuralCare
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: tokens.color.ink.primary,
            mb: 0.5,
            letterSpacing: '-0.02em',
          }}
        >
          Iniciar sesión
        </Typography>
        <Typography
          sx={{ fontSize: '0.875rem', color: tokens.color.ink.muted, mb: 4 }}
        >
          Introduce tus credenciales de administrador
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography
              component="label"
              htmlFor="username"
              sx={{
                display: 'block',
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: tokens.color.ink.secondary,
                mb: 0.75,
              }}
            >
              Usuario
            </Typography>
            <TextField
              id="username"
              fullWidth
              autoFocus
              autoComplete="username"
              placeholder="admin"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.9375rem' } }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              component="label"
              htmlFor="password"
              sx={{
                display: 'block',
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: tokens.color.ink.secondary,
                mb: 0.75,
              }}
            >
              Contraseña
            </Typography>
            <TextField
              id="password"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                    >
                      {showPassword
                        ? <VisibilityOff sx={{ fontSize: 18 }} />
                        : <Visibility sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.9375rem' } }}
            />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: 1.25,
              fontSize: '0.9375rem',
              fontWeight: 600,
            }}
          >
            {loading ? (
              <CircularProgress size={20} thickness={3} sx={{ color: 'inherit' }} />
            ) : (
              'Entrar'
            )}
          </Button>
        </Box>

        {/* Credenciales por defecto — solo en dev/staging */}
        <Box
          sx={{
            mt: 4,
            p: 2,
            borderRadius: tokens.radius.md,
            backgroundColor: tokens.color.surface.subtle,
            border: `1px solid ${tokens.color.border.default}`,
          }}
        >
          <Typography
            variant="overline"
            sx={{ display: 'block', mb: 0.75, color: tokens.color.ink.muted }}
          >
            Credenciales por defecto
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: tokens.font.mono, fontSize: '0.8rem', color: tokens.color.ink.secondary }}
          >
            admin / admin123
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
